import type { ConnectionState } from '@bn-games/room-client';
import { BN_AUTH_CHANGE, fetchMe } from '@bn-games/auth-client';
import { LobbySocket, type InvitePushListener, type PresenceListener } from './LobbySocket.js';

export interface LobbyConnectionOptions {
  readonly serverUrl: string;
}

/**
 * The page's one `/ws/lobby` connection. Every consumer on a page — the
 * invite dock, the friends list, an invite sender — shares it, so a tab holds
 * at most one lobby socket no matter how many surfaces are mounted or how
 * often the app switches scenes.
 */
export interface LobbyConnection {
  /**
   * Adds a set of ids to the page's watch list and returns its removal. The
   * socket watches the union of every live watcher, and only sends
   * `LOBBY_HELLO` while that union is non-empty.
   */
  watch(userIds: readonly string[]): () => void;
  onPresence(listener: PresenceListener): () => void;
  onInvitePush(listener: InvitePushListener): () => void;
  sendInvite(inviteId: string, toUserId: string): void;
  getState(): ConnectionState;
}

class PageLobbyConnection implements LobbyConnection {
  private readonly socket: LobbySocket;
  private readonly watchers = new Map<number, readonly string[]>();
  private nextWatcherId = 0;
  // Bumped on every auth re-evaluation so a slow fetchMe() that resolves after
  // a newer one (or after a pagehide) can't act on a stale answer.
  private generation = 0;
  private pageHidden = false;
  private connectedAs: string | null = null;
  private readonly onAuthChange = (): void => void this.evaluate();
  private readonly onPageHide = (): void => {
    this.pageHidden = true;
    this.generation += 1;
    this.stop();
  };
  private readonly onPageShow = (event: PageTransitionEvent): void => {
    if (!event.persisted) return;
    this.pageHidden = false;
    void this.evaluate();
  };

  constructor(serverUrl: string) {
    this.socket = new LobbySocket(serverUrl);
    window.addEventListener(BN_AUTH_CHANGE, this.onAuthChange);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pageshow', this.onPageShow);
    void this.evaluate();
  }

  watch(userIds: readonly string[]): () => void {
    const id = this.nextWatcherId;
    this.nextWatcherId += 1;
    this.watchers.set(id, userIds);
    this.syncWatchList();
    return () => {
      if (this.watchers.delete(id)) this.syncWatchList();
    };
  }

  onPresence(listener: PresenceListener): () => void {
    return this.socket.onPresence(listener);
  }

  onInvitePush(listener: InvitePushListener): () => void {
    return this.socket.onInvitePush(listener);
  }

  sendInvite(inviteId: string, toUserId: string): void {
    // Without a live connection the relay is simply skipped: `POST /invites`
    // is the durable record, and a queued frame would otherwise be flushed
    // into whatever account's socket connects next.
    if (this.socket.getState() !== 'connected') return;
    this.socket.sendInvite(inviteId, toUserId);
  }

  getState(): ConnectionState {
    return this.socket.getState();
  }

  dispose(): void {
    this.generation += 1;
    window.removeEventListener(BN_AUTH_CHANGE, this.onAuthChange);
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('pageshow', this.onPageShow);
    this.stop();
  }

  private async evaluate(): Promise<void> {
    this.generation += 1;
    const generation = this.generation;
    const profile = await fetchMe();
    if (generation !== this.generation || this.pageHidden) return;

    if (profile === null) {
      this.stop();
      return;
    }
    if (this.connectedAs === profile.userId) return;
    // A different account signed in on this page: the open socket carries the
    // previous account's ticket, so it has to be replaced, not reused.
    this.stop();
    this.connectedAs = profile.userId;
    this.socket.connect();
  }

  private stop(): void {
    if (this.connectedAs === null) return;
    this.connectedAs = null;
    this.socket.disconnect();
  }

  private syncWatchList(): void {
    const union = new Set<string>();
    for (const ids of this.watchers.values()) {
      for (const id of ids) union.add(id);
    }
    this.socket.watch([...union]);
  }
}

let instance: PageLobbyConnection | null = null;

/**
 * Starts the page-lifetime lobby connection, or returns the one already
 * started — later calls ignore their `serverUrl`, since every consumer on a
 * page resolves the same one. The connection itself stays idle until
 * `fetchMe()` confirms a signed-in profile, follows `BN_AUTH_CHANGE`, closes
 * on `pagehide` (an open socket blocks bfcache, and a prompt close records
 * `last_seen_at` promptly) and reopens on a bfcache `pageshow`.
 *
 * Discord Activity / Capacitor gating deliberately lives with the callers in
 * `ui-shell`: this package doesn't depend on `@bn-games/discord-activity`.
 */
export function startLobbyConnection(options: LobbyConnectionOptions): LobbyConnection {
  if (instance === null) instance = new PageLobbyConnection(options.serverUrl);
  return instance;
}

/** The page's lobby connection, or `null` when nothing has started it. */
export function getLobbyConnection(): LobbyConnection | null {
  return instance;
}

/** Tears down the singleton and its window listeners between tests. */
export function resetLobbyConnectionForTests(): void {
  if (instance !== null) instance.dispose();
  instance = null;
}
